import React, { useRef, useState } from 'react';
import '../restaurant/css/AddRestaurant.css';
import axiosGroceryInstance from '../restaurant/AxiosGroceryInstance';
import axiosRestaurantInstance from '../restaurant/AxiosRestaurantInstance';
import { GROCERY_API_PATHS } from '../constants/apiPaths';
import { getAuthHeaders } from './storeApi';

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

const AddStore = ({ onClose, createdById, onSuccess, embedded = false }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [storeName, setStoreName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isDefault, setIsDefault] = useState('false');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const addressSearchTimeoutRef = useRef(null);
  const [isHomeDeliveryAvailable, setIsHomeDeliveryAvailable] = useState(true);

  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    setAddressSearch(value);
    setAddressSuggestions([]);

    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }

    if (value.trim().length < 2) return;

    addressSearchTimeoutRef.current = setTimeout(async () => {
      addressSearchTimeoutRef.current = null;
      try {
        setAddressSearchLoading(true);
        const response = await axiosRestaurantInstance.get('/address/suggest', {
          params: { address: value.trim() },
        });
        const list = response.data?.suggestions || response.data?.predictions || [];
        setAddressSuggestions(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error?.response?.data || error?.message);
        setAddressSuggestions([]);
      } finally {
        setAddressSearchLoading(false);
      }
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
  };

  const handleAddressSelect = async (placeId) => {
    try {
      const response = await axiosRestaurantInstance.get('/address/suggest/details', {
        params: { placeId },
      });
      const placeDetails = response.data;
      if (!placeDetails) {
        alert('Address details not found');
        return;
      }
      setAddressLine1(placeDetails.addressLine1 || '');
      setAddressLine2(placeDetails.addressLine2 || '');
      setPostalCode(placeDetails.postalCode || '');
      setCity(placeDetails.city || '');
      setState(placeDetails.state || '');
      setCountry(placeDetails.country || '');
      setLatitude(placeDetails.latitude ?? placeDetails.lat ?? '');
      setLongitude(placeDetails.longitude ?? placeDetails.lon ?? '');
      setAddressSuggestions([]);
      setAddressSearch('');
    } catch (error) {
      console.error('Error fetching place details:', error?.response?.data || error?.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!(city || '').trim()) {
      alert('Please enter city.');
      return;
    }
    if (!(addressLine1 || '').trim()) {
      alert('Please enter address line 1.');
      return;
    }

    const effectiveCreatedBy = createdById || user?.userId;
    if (!effectiveCreatedBy) {
      alert('Created user is missing. Please sign in again.');
      return;
    }

    const contactDigits = String(contactNo || '').replace(/\D/g, '');
    if (contactDigits.length !== 10) {
      alert('Please enter a valid 10-digit contact number.');
      return;
    }
    const contactNoNum = parseInt(contactDigits, 10);
    const code =
      (shortCode || '').trim() || (storeName || '').trim().substring(0, 2).toUpperCase();
    const latParsed = latitude !== '' && latitude != null ? parseFloat(String(latitude), 10) : NaN;
    const lonParsed = longitude !== '' && longitude != null ? parseFloat(String(longitude), 10) : NaN;
    if (!Number.isFinite(latParsed) || !Number.isFinite(lonParsed)) {
      alert('Latitude and longitude are required. Search and select an address to set them.');
      return;
    }

    const storeData = {
      storeName: (storeName || '').trim(),
      shortCode: code,
      contactNo: contactNoNum,
      email: (email || '').trim(),
      notes: (notes || '').trim() || undefined,
      status: 'INACTIVE',
      createdBy: effectiveCreatedBy,
      homeDeliveryAvailable: isHomeDeliveryAvailable,
      isDefault: isDefault === 'true' || isDefault === true,
      address: {
        addressLine1: (addressLine1 || '').trim(),
        addressLine2: (addressLine2 || '').trim(),
        postalCode: (postalCode || '').trim(),
        city: (city || '').trim(),
        state: (state || '').trim(),
        country: (country || '').trim(),
        homeDeliveryAvailable: isHomeDeliveryAvailable,
        lat: latParsed,
        lon: lonParsed,
      },
    };

    try {
      const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.stores, storeData, {
        headers: getAuthHeaders(),
      });

      const ok = response.status === 201 || response.status === 200;
      if (ok) {
        alert(response.data?.message || 'Store added successfully');
        if (typeof onSuccess === 'function') {
          onSuccess(response?.data || {});
        }
        if (onClose) onClose();
      } else {
        alert('Failed to add store');
      }
    } catch (error) {
      console.error('Error adding store:', error?.response?.data || error?.message);
      alert(
        error?.response?.data?.message ||
          'An error occurred while adding the store. Check the console for details.'
      );
    }
  };

  return (
    <div className={`container1${embedded ? ' add-restaurant-embedded' : ''}`}>
      <div className="add-restaurant-container visible">
        <form onSubmit={handleSubmit}>
          <div className="add-restaurant-form-row">
            <div className="add-restaurant-form-group">
              <label>Store Name:</label>
              <input
                type="text"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder="Enter store name"
                required
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>Short Code:</label>
              <input
                type="text"
                value={shortCode}
                onChange={(e) => setShortCode(e.target.value)}
                placeholder="Enter short code"
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>Contact Number:</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={contactNo}
                onChange={(e) =>
                  setContactNo(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                placeholder="Enter 10-digit contact number"
                required
              />
            </div>
          </div>
          <div className="add-restaurant-form-row">
            <div className="add-restaurant-form-group">
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                required
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>Is Default:</label>
              <select value={isDefault} onChange={(e) => setIsDefault(e.target.value)}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div className="add-restaurant-form-group">
              <label>Home Delivery:</label>
              <select
                value={isHomeDeliveryAvailable ? 'true' : 'false'}
                onChange={(e) => setIsHomeDeliveryAvailable(e.target.value === 'true')}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
          <div className="add-restaurant-form-row">
            <div className="add-restaurant-form-group full-width">
              <label>Notes:</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter any notes"
              />
            </div>
          </div>

          <h3>Address</h3>
          <div className="add-restaurant-form-row add-restaurant-address-search-row">
            <div className="add-restaurant-form-group add-restaurant-address-search-wrap">
              <label>Search Address:</label>
              <input
                type="text"
                value={addressSearch}
                onChange={handleAddressSearchChange}
                placeholder="Type to search address..."
                autoComplete="off"
                className="add-restaurant-address-search-input"
              />
              {addressSearchLoading && (
                <span className="add-restaurant-address-loading">Searching...</span>
              )}
              {addressSuggestions.length > 0 && (
                <ul className="address-suggestions-dropdown">
                  {addressSuggestions.map((suggestion, index) => {
                    const placeId = suggestion.place_id || suggestion.placeId;
                    const text = suggestion.description || suggestion.formatted_address || '';
                    return (
                      <li key={placeId || index} onClick={() => handleAddressSelect(placeId)}>
                        {text}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div className="add-restaurant-form-row">
            <div className="add-restaurant-form-group">
              <label>Address Line 1:</label>
              <input
                type="text"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="Enter address line 1"
                required
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>Address Line 2:</label>
              <input
                type="text"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                placeholder="Enter address line 2"
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>City:</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter city"
                required
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>State:</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="Enter state"
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>Postal Code:</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="Enter postal code"
              />
            </div>
            <div className="add-restaurant-form-group">
              <label>Country:</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Enter country"
              />
            </div>
          </div>

          <div className="add-restaurant-form-row">
            <div className="add-restaurant-coords-pair">
              <div className="add-restaurant-form-group">
                <label>Longitude:</label>
                <input
                  type="text"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="Enter longitude"
                  readOnly
                />
              </div>
              <div className="add-restaurant-form-group">
                <label>Latitude:</label>
                <input
                  type="text"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="Enter latitude"
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="add-restaurant-submit-button">
            <button type="submit">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStore;
